import { useId } from "react";
import type { ReactNode } from "react";

export interface RangeSliderProps {
  /** Lower bound of the scale. */
  min?: number;
  /** Upper bound of the scale. */
  max?: number;
  /** Snap increment. */
  step?: number;
  /** Current `[lower, upper]` selection. */
  value: [number, number];
  onChange: (value: [number, number]) => void;
  /** Render a value (shown under each thumb). Defaults to the raw number. */
  formatValue?: (v: number) => ReactNode;
  ariaLabelMin?: string;
  ariaLabelMax?: string;
  className?: string;
  id?: string;
}

/**
 * Dual-thumb range slider — two overlaid native `<input type="range">` so it stays
 * accessible and keyboard-driven. The thumbs can't cross; the lower input is raised
 * above the upper one once it passes the midpoint so it stays grabbable at the top end.
 */
export function RangeSlider({
  min = 0,
  max = 100,
  step = 1,
  value,
  onChange,
  formatValue,
  ariaLabelMin = "Minimum",
  ariaLabelMax = "Maximum",
  className,
  id,
}: RangeSliderProps) {
  const autoId = useId();
  const baseId = id ?? autoId;
  const [lo, hi] = value;
  const span = max - min || 1;
  const loPct = ((lo - min) / span) * 100;
  const hiPct = ((hi - min) / span) * 100;
  const fmt = formatValue ?? ((v: number) => v);
  // When the thumbs overlap, keep the one the user is likely reaching for on top:
  // the lower thumb once it's past the midpoint, the upper thumb otherwise.
  const lowerOnTop = lo > min + span / 2;

  return (
    <div className={["fd-range", className].filter(Boolean).join(" ")}>
      <div className="fd-range__track">
        <div className="fd-range__fill" style={{ left: `${loPct}%`, right: `${100 - hiPct}%` }} />
        <input
          type="range"
          className="fd-range__input"
          style={{ zIndex: lowerOnTop ? 4 : 3 }}
          min={min}
          max={max}
          step={step}
          value={lo}
          id={`${baseId}-min`}
          aria-label={ariaLabelMin}
          onChange={(e) => onChange([Math.min(Number(e.target.value), hi), hi])}
        />
        <input
          type="range"
          className="fd-range__input"
          style={{ zIndex: lowerOnTop ? 3 : 4 }}
          min={min}
          max={max}
          step={step}
          value={hi}
          id={`${baseId}-max`}
          aria-label={ariaLabelMax}
          onChange={(e) => onChange([lo, Math.max(Number(e.target.value), lo)])}
        />
      </div>
      <div className="fd-range__values">
        <span>{fmt(lo)}</span>
        <span>{fmt(hi)}</span>
      </div>
    </div>
  );
}
